import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function PhoneAnswering() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showIndustriesModal, setShowIndustriesModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setShowServiceModal(true);
  };

  const faqs = [
    {
      question: "How does AI phone answering differ from a traditional answering service?",
      answer: "Unlike traditional answering services that rely on human operators working in shifts, our AI phone answering service provides instant, consistent responses 24/7 without breaks, holidays, or sick days. The AI can handle unlimited simultaneous calls, never puts customers on hold, and costs a fraction of hiring full-time staff. It learns your business specifics and delivers personalized responses while seamlessly escalating complex issues to your team when needed."
    },
    {
      question: "Can the AI handle multiple calls at the same time?",
      answer: "Yes! One of the biggest advantages of our AI phone answering service is its ability to handle unlimited simultaneous calls. Whether you receive 1 call or 100 calls at the same time, every caller gets immediate attention without busy signals or hold times. This is especially valuable during peak hours, promotional campaigns, or emergency situations when call volume spikes unexpectedly."
    },
    {
      question: "What happens if the AI can't answer a customer's question?",
      answer: "Our AI is designed with intelligent escalation protocols. If it encounters a question beyond its knowledge base or detects customer frustration, it smoothly transfers the call to your designated team member with full context of the conversation. You can also set custom rules for which types of calls should always go to humans, such as complaints, refund requests, or VIP customers."
    },
    {
      question: "How quickly can I set up the phone answering service?",
      answer: "Most small businesses are up and running within 24-48 hours. The setup process involves: (1) a brief consultation call to understand your business needs, (2) configuring your call scripts and routing rules, (3) training the AI on your FAQs and procedures, and (4) testing with sample calls. We provide a dedicated phone number or can forward your existing number to our system."
    },
    {
      question: "Can I customize what the AI says to callers?",
      answer: "Absolutely! You have complete control over your AI receptionist's personality, tone, and scripts. Customize greetings, responses to common questions, hold messages, and transfer protocols. You can make it sound professional and formal or friendly and casual—whatever matches your brand. Updates to scripts can be made anytime through our dashboard and take effect immediately."
    },
    {
      question: "Does the service include voicemail and message taking?",
      answer: "Yes! When you're unavailable or calls need follow-up, the AI can take detailed messages including caller name, contact information, reason for calling, and urgency level. All messages are instantly transcribed and sent to you via email, SMS, or your preferred communication channel. You can also access a dashboard with full call recordings and transcripts for quality assurance and training purposes."
    },
    {
      question: "What if I already have a business phone number?",
      answer: "No problem! We can work with your existing phone number in two ways: (1) Forward your current number to our AI system, or (2) Use our system as an overflow when your lines are busy. You keep your existing number and branding while gaining the benefits of AI-powered call handling. Setup is simple and doesn't require changing your published contact information."
    },
    {
      question: "How much does the phone answering service cost?",
      answer: "Our pricing is transparent and affordable for small businesses. Plans start at $99/month for up to 100 calls, with higher tiers available as your business grows. Unlike traditional answering services that charge per minute or per call, our flat-rate pricing gives you predictable costs. There are no setup fees, no long-term contracts, and you can upgrade or downgrade anytime. Most businesses save 60-80% compared to hiring a receptionist or using traditional answering services."
    }
  ];

  return (
    <>
      <SEO
        title="AI Phone Answering Service | 24/7 Automated Call Handling"
        description="Never miss a call with our AI phone answering service. Professional call handling, message taking, and customer support available 24/7. Affordable and reliable."
        canonical="/ai-phone-answering-service"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Phone Answering Service',
              description: '24/7 AI-powered phone answering service for professional call handling and customer support',
              provider: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: import.meta.env.VITE_SITE_URL || 'https://example.com'
              },
              areaServed: 'Worldwide',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/ai-phone-answering-service`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'How does AI phone answering work?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI phone answering uses advanced voice recognition and natural language processing to answer calls, understand caller needs, and provide appropriate responses or route calls to the right department.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'Is AI phone answering available 24/7?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes, AI phone answering service operates 24 hours a day, 7 days a week, ensuring you never miss important calls even outside business hours.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'Can AI handle multiple calls simultaneously?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes, AI phone answering can handle unlimited simultaneous calls, eliminating busy signals and wait times for your customers.'
                  }
                }
              ]
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
                  <i className="ri-phone-line text-primary"></i>
                  <span className="text-sm font-medium text-primary">24/7 Call Coverage</span>
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  Never Miss Another Call With AI Phone Answering
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Professional phone answering service powered by AI. Handle unlimited calls simultaneously, capture every lead, and provide exceptional customer service—all without hiring additional staff.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => setIsCalendarOpen(true)}
                    className="px-8 py-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                  >
                    <i className="ri-calendar-check-line text-xl"></i>
                    Book My Free Call
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <Link
                    to="/#pricing"
                    className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg border-2 border-gray-200 hover:border-primary transition-all whitespace-nowrap cursor-pointer"
                  >
                    View Pricing
                  </Link>
                </div>
              </div>
              <div className="relative">
                <img
                  src="/readdy-assets/phone-answering-hero-001.jpg"
                  alt="Phone Answering Service"
                  className="w-full h-auto rounded-2xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What Is Section */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What Is an AI Phone Answering Service for Small Businesses?</h2>
            <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
              <p>
                An AI phone answering service is an intelligent virtual receptionist that answers your business calls using advanced artificial intelligence and natural language processing. Unlike traditional voicemail or basic IVR systems, our AI understands context, responds naturally to customer questions, and takes appropriate actions—all while sounding remarkably human.
              </p>
              <p>
                For small businesses, this technology is a game-changer. It eliminates the need to hire, train, and manage receptionist staff while providing superior call coverage. Whether you're a solo entrepreneur, a growing startup, or an established small business, our AI phone answering service ensures every caller receives immediate, professional attention regardless of when they call or how busy you are. Add <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">website chat</Link> to capture leads from every channel.
              </p>
              <p>
                The system integrates seamlessly with your existing phone number and business tools. It can answer common questions, <Link to="/ai-appointment-scheduling" className="text-primary hover:underline font-medium">schedule appointments</Link>, qualify leads, take messages, route calls to the right team member, and even process simple transactions. All interactions are logged, transcribed, and accessible through an intuitive dashboard, giving you complete visibility into your customer communications.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How Our AI Phone Answering Service Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get up and running in days, not weeks. Our streamlined process makes it easy to deploy professional phone answering for your business.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Setup & Configuration',
                  description: 'We configure your AI receptionist with your business information, call scripts, and routing rules. Forward your existing number or use a new dedicated line.',
                  icon: 'ri-settings-3-line'
                },
                {
                  step: '02',
                  title: 'AI Training',
                  description: 'Our AI learns about your products, services, pricing, and common customer questions. We customize responses to match your brand voice and tone.',
                  icon: 'ri-brain-line'
                },
                {
                  step: '03',
                  title: 'Call Handling',
                  description: 'When customers call, the AI answers instantly, understands their needs, provides information, schedules appointments, or routes calls to your team.',
                  icon: 'ri-customer-service-2-line'
                },
                {
                  step: '04',
                  title: 'Insights & Optimization',
                  description: 'Review call transcripts, analytics, and customer insights. Continuously improve responses and optimize call handling based on real data.',
                  icon: 'ri-line-chart-line'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${item.icon} text-2xl text-primary`}></i>
                  </div>
                  <div className="text-sm font-bold text-primary mb-2">STEP {item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Powerful Features for Small Businesses</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to provide professional phone support without the overhead of traditional staffing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: 'Live Call Handling',
                  description: 'Answer every call in real-time with natural, conversational AI that understands context and responds appropriately to customer inquiries.',
                  icon: 'ri-phone-line',
                  color: 'bg-blue-500'
                },
                {
                  title: 'Intelligent Call Routing',
                  description: 'Route calls to the right team member based on caller needs, department, urgency, or custom rules you define.',
                  icon: 'ri-route-line',
                  color: 'bg-purple-500'
                },
                {
                  title: 'Voicemail Transcription',
                  description: 'Automatically transcribe voicemails and messages into text, delivered instantly to your email or SMS for quick review.',
                  icon: 'ri-voiceprint-line',
                  color: 'bg-green-500'
                },
                {
                  title: 'After-Hours Coverage',
                  description: 'Provide 24/7 phone support even when your office is closed. Capture leads, answer questions, and schedule callbacks automatically.',
                  icon: 'ri-time-line',
                  color: 'bg-orange-500'
                },
                {
                  title: 'Custom Call Scripts',
                  description: 'Fully customize greetings, responses, and call flows to match your brand voice and business processes.',
                  icon: 'ri-file-text-line',
                  color: 'bg-pink-500'
                },
                {
                  title: 'Call Analytics & Reporting',
                  description: 'Track call volume, duration, outcomes, and customer satisfaction. Access full recordings and transcripts for quality assurance.',
                  icon: 'ri-bar-chart-box-line',
                  color: 'bg-teal-500'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all">
                  <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-6`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-primary/5 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Benefits for Your Small Business</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transform your phone system from a cost center into a competitive advantage.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img
                  src="/readdy-assets/phone-benefits-001.jpg"
                  alt="Business Benefits"
                  className="w-full h-auto rounded-2xl shadow-xl object-cover"
                />
              </div>
              <div className="space-y-6">
                {[
                  {
                    title: 'Never Miss a Lead',
                    description: 'Capture every potential customer, even during peak hours or after business hours. Studies show 80% of callers won\'t leave a voicemail—our AI ensures you never lose an opportunity.',
                    icon: 'ri-user-add-line'
                  },
                  {
                    title: 'Reduce Staffing Costs',
                    description: 'Save 60-80% compared to hiring full-time receptionists. No salaries, benefits, training costs, or coverage gaps during vacations and sick days.',
                    icon: 'ri-money-dollar-circle-line'
                  },
                  {
                    title: 'Scale Effortlessly',
                    description: 'Handle 1 call or 1,000 calls with the same efficiency. No need to hire additional staff as your business grows or during seasonal peaks.',
                    icon: 'ri-rocket-line'
                  },
                  {
                    title: 'Improve Customer Experience',
                    description: 'Eliminate hold times, busy signals, and voicemail frustration. Every caller gets immediate, professional attention that reflects well on your brand.',
                    icon: 'ri-star-smile-line'
                  },
                  {
                    title: 'Focus on Core Business',
                    description: 'Stop interrupting your work to answer routine calls. Let AI handle the basics while you focus on high-value activities that grow your business.',
                    icon: 'ri-focus-3-line'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                      <i className={`${benefit.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete Your Business Communication Stack</h2>
              <p className="text-lg text-gray-600">
                Combine phone answering with other AI services for comprehensive customer support.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Link to="/services/ai-chatbot" className="group bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className="ri-chat-3-line text-2xl text-primary"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">AI Chatbot</h3>
                <p className="text-gray-600 mb-4">Handle website inquiries 24/7 with intelligent chat support that complements your phone service.</p>
                <div className="flex items-center gap-2 text-primary font-semibold">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </Link>

              <Link to="/ai-appointment-scheduling" className="group bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className="ri-calendar-check-line text-2xl text-primary"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">Appointment Scheduling</h3>
                <p className="text-gray-600 mb-4">Let callers book appointments directly through the AI phone system with automatic calendar sync.</p>
                <div className="flex items-center gap-2 text-primary font-semibold">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </Link>

              <Link to="/services/ai-receptionist" className="group bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className="ri-user-voice-line text-2xl text-primary"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">AI Receptionist</h3>
                <p className="text-gray-600 mb-4">Complete front-desk automation combining phone, chat, and scheduling in one unified solution.</p>
                <div className="flex items-center gap-2 text-primary font-semibold">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about our AI phone answering service.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-primary flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-8 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-primary to-secondary text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Transform Your Phone System?</h2>
            <p className="text-xl mb-8 opacity-90">
              Join hundreds of small businesses using AI to provide exceptional phone support. Start your free trial today—no credit card required.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
              <i className="ri-calendar-check-line text-xl"></i>
                Book My Free Call
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setShowIndustriesModal(true)}
          onOpenHowItWorks={() => setShowHowItWorksModal(true)}
        />

        <ServiceModal
          isOpen={showServiceModal}
          onClose={() => setShowServiceModal(false)}
          title={selectedService}
        />

        <IndustriesModal
          isOpen={showIndustriesModal}
          onClose={() => setShowIndustriesModal(false)}
        />

        <HowItWorksModal
          isOpen={showHowItWorksModal}
          onClose={() => setShowHowItWorksModal(false)}
        />

        <CalendarModal
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
        />
      </div>
    </>
  );
}